import React, { useState, useEffect } from 'react';
import { ScrollText, Search, Flag, Eye, Trash2, Clock, DollarSign } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const GenerationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    try {
      // Fetch user messages
      const { data, error } = await supabase
        .from('messages')
        .select(`
          id, 
          content, 
          role, 
          created_at,
          user_id
        `)
        .eq('role', 'user')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      
      // Fetch profiles separately to avoid complex join issues if schema is slightly different
      const userIds = [...new Set((data || []).map(m => m.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', userIds);
      
      const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p.username]));
      
      const enrichedLogs = (data || []).map(log => ({
        ...log,
        username: profileMap[log.user_id] || 'unknown'
      }));

      setLogs(enrichedLogs);
    } catch (err) {
      console.error("[GenerationLogs] Error:", err);
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = (logs || []).filter(log => 
    (log.content || "").toLowerCase().includes(search.toLowerCase()) ||
    (log.username || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-syne font-bold">Generation Logs</h1>
          <p className="text-sonas-muted text-xs">AI prompt history and system usage.</p>
        </div>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sonas-dim" />
          <Input 
            placeholder="Search prompts or users..." 
            className="pl-10 bg-sonas-surface2 border-sonas-border rounded-xl"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-4">
        {filteredLogs.map((log) => (
          <div key={log.id} className="bg-sonas-surface border border-sonas-border rounded-2xl p-5 hover:border-sonas-accent/20 transition-all group">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-sonas-text">@{log.username}</span>
                  <div className="flex items-center gap-1 text-[10px] text-sonas-dim">
                    <Clock size={10} /> {log.created_at ? new Date(log.created_at).toLocaleString() : 'Just now'}
                  </div>
                </div>
                <p className="text-sm text-sonas-muted leading-relaxed line-clamp-2 italic">
                  "{log.content}"
                </p>
              </div>

              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-white/5"><Eye size={16} /></Button>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-red-500/10 text-red-500"><Flag size={16} /></Button>
              </div>
            </div>
          </div>
        ))}

        {!loading && filteredLogs.length === 0 && (
          <div className="py-20 text-center text-sonas-dim text-sm uppercase font-bold tracking-widest">No logs found</div>
        )}
      </div>
    </div>
  );
};

export default GenerationLogs;